import { useMemo, useImperativeHandle, createRef } from 'react'
import fpPickBy from 'lodash/fp/pickBy'
import type { TFunction } from 'i18next'
import type { TodistuksetUser } from 'todistukset-common/src/vtj/types/user'
import type { ValidationError } from 'todistukset-common/src/vtj/form-validation.util'
import {
  getPropertyTranslationKey,
  type getValidationErrorFromValidator,
} from 'todistukset-ui/src/vtj/components/order-wizard/validation'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'

export type FirstErrorImperativeHandle<RefType = HTMLInputElement> = {
  getFirstErrorField: () => RefType | null
}

/**
 * Forwarded ref multiplexer. Allows the OrderWizardPage frame to focus
 * the first field within a contained Step, without needing to understand
 * the Step's internals.
 */
export function useFirstErrorImperativeHandle<
  Keys extends string,
  FormType,
  RefType = HTMLInputElement
>(
  ref: React.ForwardedRef<FirstErrorImperativeHandle<RefType>>,
  form: FormType,
  getErrors: (
    form: FormType,
    user: TodistuksetUser
  ) => Record<Keys, ReturnType<typeof getValidationErrorFromValidator>>,
  fields: readonly Keys[]
): Record<Keys, React.RefObject<RefType>> {
  const refs = useMemo(
    () => Object.fromEntries(fields.map((k) => [k, createRef<RefType>()])),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  ) as Record<Keys, React.RefObject<RefType>>
  const user = useStore().common.loggedInUser.get()
  useImperativeHandle(
    ref,
    () => ({
      getFirstErrorField() {
        const errors = getErrors(form, user)
        return (
          Object.values(getRefsWithErrors(refs, errors))[0]?.current ?? null
        )
      },
    }),
    [form, user, refs, getErrors]
  )
  return refs
}

function getRefsWithErrors<T extends Record<string, unknown>, R>(
  refs: Record<keyof T, R>,
  errors: Record<keyof T, ReturnType<typeof getValidationErrorFromValidator>>
): Partial<Record<keyof T, R>> {
  return fpPickBy<typeof refs>(
    (_value: R, key) => errors[key] !== undefined,
    refs
  )
}

/**
 * Assignable to suomifi-ui-components form fields
 */
export function getErrorStatus<
  FieldsType extends keyof Record<string, unknown>
>({
  field,
  errors,
  showErrors,
  t,
}: {
  field: FieldsType
  errors: Record<FieldsType, ValidationError | undefined>
  showErrors: boolean
  t: TFunction
}): {
  status: 'default' | 'error'
  statusText?: string
} {
  const error = errors[field]
  const hasError = error !== undefined
  if (showErrors && hasError) {
    return {
      status: 'error',
      statusText: t(error.errorTranslationKey, {
        ...(error.errorTranslationParams ?? {}),
        field: t(getPropertyTranslationKey(error)),
      }),
    }
  }
  return {
    status: 'default',
  }
}
